import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Field, Skeleton, StatusBadge } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime } from '../../lib/format.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

export interface SupportGrant {
  id: string;
  granted_by_name: string;
  hours: number;
  note: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  revoked_by_name: string | null;
  used_by: string[];
  open: boolean;
}
export interface SupportStatus {
  open: SupportGrant | null;
  recent: SupportGrant[];
}
const HOURS = [1, 2, 4, 8, 24, 72] as const;

/**
 * Manage → ERP PRO support: the owner lets support into the business for a few hours, sees
 * who came in, and can close it at any time. Without this, support cannot come in.
 */
export function Support() {
  const { data, reload } = useAction<SupportStatus>('support.status');
  const { run, busy } = useSave();
  const [hours, setHours] = useState<number>(2);
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState(false);
  const open = data?.open ?? null;
  return (
    <ManageLayout title={t('manage.support')} subtitle={t('support.subtitle')}>
      <div className="support-page">
        {!data ? (
          <Skeleton h={200} r="var(--radius-lg)" />
        ) : open ? (
          <section className="card support-open" aria-labelledby="support-now">
            <div className="card-head">
              <h2 id="support-now">
                <Icon name="eye" size={18} /> {t('support.open_title')}
              </h2>
              <StatusBadge tone="warn" icon="clock" label={t('support.until', { time: dateTime(open.expires_at) })} />
            </div>
            <p>{t('support.open_body', { name: open.granted_by_name, at: dateTime(open.created_at) })}</p>
            {open.note ? <p className="muted">“{open.note}”</p> : null}
            <p className="muted">{open.used_by.length ? t('support.came_in', { names: open.used_by.join(', ') }) : t('support.not_yet')}</p>
            <div className="support-actions">
              <Button variant="danger" icon="lock" onClick={() => setClosing(true)}>
                {t('support.close')}
              </Button>
            </div>
          </section>
        ) : (
          <section className="card" aria-labelledby="support-allow">
            <div className="card-head">
              <h2 id="support-allow">
                <Icon name="shield" size={18} /> {t('support.allow_title')}
              </h2>
              <StatusBadge tone="ok" icon="lock" label={t('support.closed')} />
            </div>
            <p className="muted">{t('support.allow_body')}</p>
            <div className="chips" role="group" aria-label={t('support.how_long')}>
              {HOURS.map((h) => (
                <button key={h} type="button" className={`chip${hours === h ? ' active' : ''}`} aria-pressed={hours === h} onClick={() => setHours(h)}>
                  {tn('support.hours', h)}
                </button>
              ))}
            </div>
            <Field label={t('support.note')} hint={t('support.note_hint')}>
              <input className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <div className="support-actions">
              <Button
                variant="primary"
                icon="checkCircle"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await action('support.grant', { hours, note: note.trim() || null });
                    setNote('');
                    await reload();
                  }, t('support.allowed'))
                }
              >
                {t('support.allow', { hours: tn('support.hours', hours) })}
              </Button>
            </div>
          </section>
        )}
        {data?.recent.length ? (
          <section className="card" aria-labelledby="support-history">
            <div className="card-head">
              <h2 id="support-history">{t('support.history')}</h2>
            </div>
            <ul className="support-list">
              {data.recent.map((g) => (
                <li key={g.id}>
                  <div>
                    <strong>{t('support.history_line', { name: g.granted_by_name, hours: tn('support.hours', g.hours) })}</strong>
                    <div className="muted small">
                      {dateTime(g.created_at)}
                      {g.used_by.length ? ` · ${t('support.came_in', { names: g.used_by.join(', ') })}` : ` · ${t('support.not_used')}`}
                      {g.revoked_at ? ` · ${t('support.closed_by', { name: g.revoked_by_name ?? '–', at: dateTime(g.revoked_at) })}` : ''}
                    </div>
                  </div>
                  <StatusBadge tone={g.open ? 'warn' : 'neutral'} icon={g.open ? 'eye' : 'lock'} label={g.open ? t('support.state_open') : t('support.state_over')} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      {closing ? (
        <Confirm
          title={t('support.close_title')}
          body={t('support.close_body')}
          confirm={t('support.close')}
          danger
          busy={busy}
          onClose={() => setClosing(false)}
          onConfirm={() =>
            void run(async () => {
              await action('support.revoke', {});
              setClosing(false);
              await reload();
            }, t('support.closed_toast'))
          }
        />
      ) : null}
    </ManageLayout>
  );
}
